import React, { useState, useEffect } from 'react';
import { CompanyTable, CompanyFilter, Pagination } from '../components';
import { fetchCompanies } from '../services/api';

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
    last: false
  });
  
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    sort: 'id,desc' // Always sort by ID in descending order
  });

  // Fetch companies when component mounts or when filters/pagination change
  useEffect(() => {
    const fetchCompanyData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Prepare query parameters
        const params = {
          page: pagination.page,
          size: pagination.size,
          sort: filters.sort
        };
        
        // Add optional filters if they exist
        if (filters.name) params.name = filters.name;
        if (filters.email) params.email = filters.email;
        
        const response = await fetchCompanies(params);
        
        setCompanies(response.content);
        setPagination({
          ...pagination,
          totalPages: response.totalPages,
          totalElements: response.totalElements,
          last: response.last
        });
      } catch (err) {
        setError('Failed to fetch companies. Please try again later.');
        console.error('Error fetching companies:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanyData();
  }, [pagination.page, pagination.size, filters]);

  const handlePageChange = (newPage) => {
    setPagination({
      ...pagination,
      page: newPage
    });
  };

  const handleFilterChange = (newFilters) => {
    // Reset to first page when filters change
    setPagination({
      ...pagination,
      page: 0
    });
    
    setFilters({
      ...filters,
      ...newFilters
    });
  };

  const handleRefresh = () => {
    // Rerun the current query to refresh data
    const currentPage = pagination.page;
    setPagination({
      ...pagination,
      page: -1 // Set to invalid value temporarily
    });
    // Then reset to current page to trigger the useEffect
    setTimeout(() => {
      setPagination({
        ...pagination,
        page: currentPage
      });
    }, 0);
  };

  return (
    <div className="px-2 sm:px-0">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Sponsor Management</h2>
        <CompanyFilter 
          onFilterChange={handleFilterChange} 
          initialFilters={filters} 
        />
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
          <p>{error}</p>
        </div>
      )}
      
      <CompanyTable 
        companies={companies} 
        loading={loading}
        onRefresh={handleRefresh}
      />
      
      {!loading && companies.length > 0 && (
        <Pagination 
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}
      
      <div className="mt-6 text-center text-sm text-gray-500">
        {!loading && companies.length > 0 && (
          <p>Showing {companies.length} of {pagination.totalElements} companies</p>
        )}
      </div>
    </div>
  );
};

export default CompanyManagement; 